<template>
  <div v-if="ready" class="sp-slot-grid" :class="classModifiers">
    <div class="sp-slot-grid__days">
      <div v-for="day in days" :key="day.key" class="sp-slot-grid__day">
        <SlotDayHeading class="sp-slot-grid__heading" :date="day.date" variant="line" :show-year="showYear" />

        <div class="sp-slot-grid__slots">
          <div v-for="(slot, index) in day.slots" :key="index" class="sp-slot-grid__cell">
            <SlotButton
              class="sp-slot-grid__slot"
              :appointment-slot="slot"
              variant="flat"
              :icon-size="null"
              tooltip
              :label="slotLabel(slot, day.date)"
              @pick="pickSlot"
            />
          </div>

          <div v-if="day.moreCount" class="sp-slot-grid__cell">
            <SlotMoreButton
              class="sp-slot-grid__slot"
              :count="day.moreCount"
              :date="day.date"
              variant="flat"
              icon="chevron-down"
              :icon-size="null"
              :accessible-label="moreLabel(day)"
              analytics-element="slot-grid-more-slots"
              @show-more="showMoreSlots"
            />
          </div>

          <div v-for="index in day.fillerCount" :key="`filler-${index}`" class="sp-slot-grid__cell">
            <span class="sp-slot-grid__filler"></span>
          </div>
        </div>
      </div>
    </div>

    <SlotFullScheduleButton
      v-if="showsFullSchedule"
      class="sp-slot-grid__full-schedule"
      variant="button"
      button-variant="flat"
      :icon="fullScheduleIcon"
      :icon-size="null"
      analytics-element="finder-full-schedule"
      @open="showFullSchedule"
    >
      {{ t("view_full_schedule") }}
    </SlotFullScheduleButton>
  </div>
</template>

<script setup>
import { useMediaQuery } from "@vueuse/core";
import { computed, onMounted, ref } from "vue";
import { i18n, whenI18nReady } from "#ce/components/SpFinder/i18n";
import SlotDayHeading from "#ce/components/SpFinder/slots/SlotDayHeading.ce.vue";
import SlotFullScheduleButton from "#ce/components/SpFinder/slots/SlotFullScheduleButton.ce.vue";
import SlotMoreButton from "#ce/components/SpFinder/slots/SlotMoreButton.ce.vue";
import SlotButton from "#ce/components/SpFinder/slots/SlotButton.ce.vue";
import { formatOrdinalDate } from "#/shared/lib/helpers/localized-date";
import { formatSlotTime, toDayKey, toEntryDate } from "#ce/components/SpFinder/slots/slot-format";
import { toBoolean, toJSON } from "#ce/utils/props";
import { props as propsDefinitions } from "./props.js";

const props = defineProps(propsDefinitions);

const emit = defineEmits(["pick-slot", "show-more-slots", "show-full-schedule"]);

function t(...args) {
  return i18n.global.t(...args);
}

const ready = ref(false);

async function markReady() {
  await whenI18nReady;
  ready.value = true;
}

onMounted(markReady);

const isStacked = useMediaQuery(() => `(max-width: ${props.rowsMaxViewportWidth}px)`);
const listsTimesInColumns = useMediaQuery(() => `(min-width: ${props.columnsMinViewportWidth}px)`);
const entries = computed(() => toJSON(props.entries, true) ?? []);
const showsFullSchedule = computed(() => !toBoolean(props.noFullSchedule) && entries.value.length > 0);
const classModifiers = computed(() => ({ "--stacked": isStacked.value, "--columns": listsTimesInColumns.value }));

// Fillers pad a column to `maxSlots` + 1 rows, the height of a column with an "N more" button.
function toDay({ date, slots }) {
  const shown = slots.slice(0, props.maxSlots);
  const moreCount = slots.length - shown.length;
  const fillerCount = props.maxSlots + 1 - shown.length - (moreCount > 0 ? 1 : 0);

  return { key: toDayKey(date), date, slots: shown, moreCount, fillerCount: Math.max(fillerCount, 0) };
}

const days = computed(() => entries.value.map(toDay));

function slotLabel(slot, date) {
  return t("slot_label", {
    time: formatSlotTime(slot.start_time),
    date: formatOrdinalDate(toEntryDate(date), props.showYear),
  });
}

function moreLabel(day) {
  return t("more_label", {
    count: day.moreCount,
    date: formatOrdinalDate(toEntryDate(day.date), props.showYear),
  });
}

function pickSlot(slot) {
  emit("pick-slot", { slot });
}

function showMoreSlots({ date }) {
  emit("show-more-slots", { date });
}

function showFullSchedule() {
  emit("show-full-schedule");
}
</script>

<style lang="scss">
:host {
  display: block;
  width: 100%;
}
</style>

<style scoped lang="scss">
.sp-slot-grid {
  --slot-height: var(--sp-comp-slot-grid-slot-height, var(--sp-ref-scale-44));
  --slot-bg-color: var(--sp-comp-slot-grid-slot-bg-color, transparent);
  --slot-hover-bg-color: var(--sp-comp-slot-grid-slot-hover-bg-color, var(--sp-sys-color-primary-container));
  --slot-text-color: var(--sp-comp-slot-grid-slot-text-color, var(--sp-sys-color-link));
  --slot-hover-text-color: var(--sp-comp-slot-grid-slot-hover-text-color, var(--sp-sys-color-primary));
  --slot-border-color: var(--sp-comp-slot-grid-slot-border-color, var(--sp-sys-color-primary));
  --slot-padding: var(--sp-comp-slot-grid-slot-padding, var(--sp-ref-scale-2) var(--sp-ref-scale-12) 0);
  --slot-font-size: clamp(var(--sp-ref-font-size-small), 3vw, var(--sp-ref-font-size-semi-medium));
  --full-schedule-bg-color: var(--sp-comp-slot-grid-full-schedule-bg-color, transparent);
  --full-schedule-text-color: var(--sp-comp-slot-grid-full-schedule-text-color, var(--sp-sys-color-link));
  --full-schedule-border-color: var(--sp-comp-slot-grid-full-schedule-border-color, transparent);

  display: flex;
  flex-direction: column;
  gap: var(--sp-comp-slot-grid-gap, var(--sp-ref-scale-16));
  width: 100%;
}

.sp-slot-grid__days {
  position: relative;
  display: flex;
  width: 100%;
}

.sp-slot-grid__day {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
}

.sp-slot-grid__heading {
  --sp-comp-slot-day-heading-font-short: var(
    --sp-comp-slot-grid-heading-font-short,
    var(--sp-ref-font-weight-bold) var(--sp-sys-typescale-h4-font-size) / var(--sp-sys-typescale-h4-line-height)
      var(--sp-ref-font-family-bold)
  );
  --sp-comp-slot-day-heading-color: var(--sp-comp-slot-grid-heading-color);
  --sp-comp-slot-day-heading-margin-block-start: var(--sp-comp-slot-grid-heading-margin-block-start, 0);
  --sp-comp-slot-day-heading-margin-block-end: var(
    --sp-comp-slot-grid-heading-margin-block-end,
    var(--sp-ref-scale-16)
  );

  display: var(--sp-comp-slot-grid-heading-display, none);
}

.sp-slot-grid__slots {
  --sp-comp-slot-button-bg-color: var(--slot-bg-color);
  --sp-comp-slot-button-hover-bg-color: var(--slot-hover-bg-color);
  --sp-comp-slot-button-padding: var(--slot-padding);
  --sp-comp-slot-button-tooltip-bg-color: var(--sp-comp-slot-grid-tooltip-bg-color);
  --sp-comp-slot-button-tooltip-text-color: var(--sp-comp-slot-grid-tooltip-text-color);
  --sp-comp-slot-button-tooltip-shadow-filter: var(--sp-comp-slot-grid-tooltip-shadow-filter);
  --sp-comp-slot-button-tooltip-padding: var(
    --sp-comp-slot-grid-tooltip-padding,
    var(--sp-ref-scale-6) var(--sp-ref-scale-16) var(--sp-ref-scale-4)
  );
  --sp-comp-slot-button-tooltip-font-short: var(--sp-comp-slot-grid-tooltip-font-short);
  --sp-comp-slot-button-tooltip-font-size: var(
    --sp-comp-slot-grid-tooltip-font-size,
    var(--sp-ref-font-size-semi-medium)
  );
  --sp-comp-slot-button-tooltip-border-radius: var(
    --sp-comp-slot-grid-tooltip-border-radius,
    var(--sp-sys-shape-container)
  );
  --sp-comp-slot-button-tooltip-border-width: var(--sp-comp-slot-grid-tooltip-border-width, 0px);
  --sp-comp-slot-button-tooltip-min-height: var(--sp-comp-slot-grid-tooltip-min-height, var(--sp-ref-scale-44));
  --sp-comp-slot-button-tooltip-min-width: var(--sp-comp-slot-grid-tooltip-min-width, var(--sp-ref-scale-96));
  --sp-comp-slot-button-tooltip-justify-content: var(--sp-comp-slot-grid-tooltip-justify-content, center);
  --sp-comp-button-primary-bg-color: var(--slot-bg-color);
  --sp-comp-button-primary-hover-bg-color: var(--slot-hover-bg-color);
  --sp-comp-button-primary-text-color: var(--slot-text-color);
  --sp-comp-button-primary-hover-text-color: var(--slot-hover-text-color);
  --sp-comp-button-hover-bg-opacity: 1;
  --sp-comp-button-border-width: var(--sp-comp-slot-grid-slot-border-width, 1px);
  --sp-comp-button-border-color: var(--slot-border-color);
  --sp-comp-button-hover-border-color: var(--sp-comp-slot-grid-slot-hover-border-color, var(--slot-border-color));
  --sp-comp-button-border-radius: var(--sp-comp-slot-grid-slot-border-radius, var(--sp-ref-border-radius-xs));
  --sp-comp-button-small-height: var(--slot-height);
  --sp-comp-button-compact-height: var(--slot-height);
  --sp-comp-button-small-padding: var(--slot-padding);
  --sp-comp-button-small-font-short: var(
    --sp-comp-slot-grid-slot-font-short,
    var(--sp-sys-typescale-body-semi-bold-font-weight) var(--slot-font-size) / var(--sp-sys-typescale-body-line-height)
      var(--sp-sys-typescale-body-semi-bold-font-family)
  );
  --sp-comp-button-gap: 0;
  --sp-comp-slot-button-icon-gap: var(--sp-comp-slot-grid-slot-icon-gap, calc(0.1 * var(--slot-font-size)));
  --sp-comp-slot-more-button-icon-margin-inline: var(
    --sp-comp-slot-grid-more-icon-margin-inline,
    0 calc(-1 * var(--sp-ref-scale-10))
  );
  --sp-comp-button-prepend-icon-size: var(--sp-comp-slot-grid-slot-icon-size, calc(1.1 * var(--slot-font-size)));
  --sp-comp-button-append-icon-size: var(--sp-comp-slot-grid-more-icon-size, var(--sp-ref-scale-32));
  --sp-comp-button-prepend-icon-fill: var(--slot-text-color);
  --sp-comp-button-append-icon-fill: var(--slot-text-color);

  display: flex;
  overflow: auto;
  gap: var(--sp-comp-slot-grid-row-gap, var(--sp-ref-scale-10));
}

.sp-slot-grid__cell {
  display: flex;
  align-items: center;
  flex: none;
  justify-content: center;
  height: var(--sp-comp-slot-grid-cell-height, var(--slot-height));
}

.sp-slot-grid__slot {
  --sp-comp-button-min-width: 0;

  flex: none;
  width: max-content;
  min-width: 100%;
  max-width: var(--sp-comp-slot-grid-slot-max-width, 100%);
}

.sp-slot-grid__slot:hover {
  --sp-comp-button-prepend-icon-fill: var(--slot-hover-text-color);
  --sp-comp-button-append-icon-fill: var(--slot-hover-text-color);
}

.sp-slot-grid__filler {
  width: var(--sp-comp-slot-grid-filler-width, var(--sp-ref-scale-12));
  height: var(--sp-comp-slot-grid-filler-height, 1px);
  background: var(--sp-comp-slot-grid-filler-color, var(--sp-sys-color-outline));
}

.sp-slot-grid__full-schedule {
  --sp-comp-slot-full-schedule-button-box-shadow: var(--sp-comp-slot-grid-full-schedule-box-shadow, none);
  --sp-comp-button-primary-bg-color: var(--full-schedule-bg-color);
  --sp-comp-button-primary-hover-bg-color: var(
    --sp-comp-slot-grid-full-schedule-hover-bg-color,
    var(--full-schedule-bg-color)
  );
  --sp-comp-button-primary-text-color: var(--full-schedule-text-color);
  --sp-comp-button-primary-hover-text-color: var(
    --sp-comp-slot-grid-full-schedule-hover-text-color,
    var(--full-schedule-text-color)
  );
  --sp-comp-button-hover-bg-opacity: 1;
  --sp-comp-button-border-width: var(--sp-comp-slot-grid-full-schedule-border-width, 0);
  --sp-comp-button-border-color: var(--full-schedule-border-color);
  --sp-comp-button-hover-border-color: var(
    --sp-comp-slot-grid-full-schedule-hover-border-color,
    var(--full-schedule-border-color)
  );
  --sp-comp-button-border-radius: var(--sp-comp-slot-grid-full-schedule-border-radius, var(--sp-ref-border-radius-xs));
  --sp-comp-button-size: auto;
  --sp-comp-button-compact-height: auto;
  --sp-comp-button-padding: var(
    --sp-comp-slot-grid-full-schedule-padding,
    var(--sp-ref-scale-4) var(--sp-ref-scale-16)
  );
  --sp-comp-button-font-short: var(--sp-comp-slot-grid-full-schedule-font-short, var(--sp-sys-typescale-body-short));
  --sp-comp-button-text-decoration: var(--sp-comp-slot-grid-full-schedule-text-decoration, none);
  --sp-comp-button-focus-text-decoration: var(--sp-comp-slot-grid-full-schedule-text-decoration, none);
  --sp-comp-button-gap: 0;
  --sp-comp-slot-full-schedule-button-icon-gap: var(--sp-comp-slot-grid-full-schedule-icon-gap, var(--sp-ref-scale-6));
  --sp-comp-button-prepend-icon-size: var(--sp-comp-slot-grid-full-schedule-icon-size, var(--sp-ref-scale-32));
  --sp-comp-button-prepend-icon-fill: var(--sp-comp-slot-grid-full-schedule-icon-color, var(--sp-sys-color-primary));

  align-self: var(--sp-comp-slot-grid-full-schedule-align-self, center);
  margin-block-start: var(--sp-comp-slot-grid-full-schedule-margin-block-start, 0);
}

.sp-slot-grid.--stacked .sp-slot-grid__days {
  --fade-angle: 90deg;

  flex-direction: column;

  &::after {
    position: absolute;
    inset-block: 0;
    inset-inline-end: 0;
    width: var(--sp-comp-slot-grid-fade-width, var(--sp-ref-scale-20));
    content: "";
    background: linear-gradient(
      var(--fade-angle),
      transparent,
      var(--sp-comp-slot-grid-fade-color, var(--sp-sys-color-surface))
    );
    pointer-events: none;
  }

  &:dir(rtl) {
    --fade-angle: 270deg;
  }
}

.sp-slot-grid.--columns {
  .sp-slot-grid__day {
    padding: 0 var(--sp-comp-slot-grid-day-padding-inline, var(--sp-ref-scale-6));
    text-align: center;
  }

  .sp-slot-grid__heading {
    display: var(--sp-comp-slot-grid-column-heading-display, none);
  }

  .sp-slot-grid__slots {
    flex-direction: column;
    overflow: visible;
  }
}
</style>
